import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { blogArticleQuery, blogCursorListQuery, blogQuery } from "../graphql/blog-gql";
import { useGraphQLClient } from "../graphql/client";
import { INFINITE_LIST_QUERY_OPTIONS } from "../graphql/queryClient";
import type { IBlogQuery } from "../interface/blog";
import { locale } from "../locales";
import { ToastType } from "../types/layout";
import type { IUseBlogQuery } from "../types/queries";

/** useInfiniteQuery 페이지들을 기존 병합 결과(IBlogQuery) 형태로 합친다. blog.articles.edges 누적. */
function mergeBlogPages(pages: IBlogQuery[] | undefined): IBlogQuery | undefined {
  if (!pages?.length) return undefined;

  const firstBlog = pages[0]?.blog;
  if (!firstBlog) return pages[0];

  const lastBlog = pages[pages.length - 1].blog;

  // 단일 글 조회(articleByHandle)는 articles가 없을 수 있으므로 있을 때만 병합
  const mergedArticles = lastBlog.articles
    ? {
        ...lastBlog.articles,
        edges: pages.flatMap((page) => page.blog?.articles?.edges ?? []),
        pageInfo: lastBlog.articles.pageInfo,
      }
    : lastBlog.articles;

  return { blog: { ...lastBlog, articles: mergedArticles } } as IBlogQuery;
}

export default function useBlogQuery({ lang, openToast, initialBlogVariables = null, initialCursorListVariables = null }: IUseBlogQuery) {
  const request = useGraphQLClient();
  const queryClient = useQueryClient();

  const [blogVariables, setBlogVariables] = useState<Record<string, unknown> | null>(initialBlogVariables);

  // cache-first(staleTime: Infinity)로 목록 → 상세 → 뒤로가기 시 페이지/스크롤 복원
  const blogOptions = (variables: Record<string, unknown> | null) =>
    infiniteQueryOptions({
      ...INFINITE_LIST_QUERY_OPTIONS,
      queryKey: ["shopifyV2", "blog", variables],
      // articleHandle이 있으면 단건 조회(articleByHandle), 없으면 목록 조회
      queryFn: ({ pageParam }) =>
        request<IBlogQuery>(variables?.articleHandle ? blogArticleQuery : blogQuery, { ...variables, after: pageParam }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        lastPage.blog?.articles?.pageInfo?.hasNextPage ? (lastPage.blog.articles.pageInfo.endCursor ?? undefined) : undefined,
    });

  const blogInfinite = useInfiniteQuery({ ...blogOptions(blogVariables), enabled: blogVariables !== null });

  useEffect(() => {
    if (blogInfinite.error) openToast({ message: locale(lang).error.server, type: ToastType.Error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogInfinite.error]);

  const blogData = useMemo(() => mergeBlogPages(blogInfinite.data?.pages), [blogInfinite.data]);

  // Apollo previousData 대응: 로딩 중(변수 변경 등) 직전 데이터를 보관해 노출
  const previousBlogDataRef = useRef<IBlogQuery | undefined>(undefined);

  useEffect(() => {
    if (blogData) previousBlogDataRef.current = blogData;
  }, [blogData]);

  const [cursorListVariables, setCursorListVariables] = useState<Record<string, unknown> | null>(initialCursorListVariables);

  // 목록/상세가 같은 variables 로 부르면 같은 캐시를 쓰도록, articleHandle 이 섞이지 않은 별도 키를 쓴다.
  const cursorListOptions = (variables: Record<string, unknown> | null) =>
    queryOptions({
      ...INFINITE_LIST_QUERY_OPTIONS,
      queryKey: ["shopifyV2", "blogCursorList", variables],
      queryFn: () => request<IBlogQuery>(blogCursorListQuery, variables ?? {}),
    });

  const cursorListResult = useQuery({ ...cursorListOptions(cursorListVariables), enabled: cursorListVariables !== null });

  useEffect(() => {
    if (cursorListResult.error) openToast({ message: locale(lang).error.server, type: ToastType.Error });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorListResult.error]);

  const fetchCursorList = async (variables: Record<string, unknown>) => {
    setCursorListVariables(variables);
    return { data: await queryClient.fetchQuery(cursorListOptions(variables)) };
  };

  const fetchBlog = async (variables: Record<string, unknown>) => {
    setBlogVariables(variables);
    const result = await queryClient.fetchInfiniteQuery(blogOptions(variables));
    return { data: mergeBlogPages(result.pages) };
  };

  return {
    fetchBlog,
    blogData,
    blogLoading: blogInfinite.isFetching,
    fetchMoreBlog: () => blogInfinite.fetchNextPage(),
    blogPreviousData: previousBlogDataRef.current,
    fetchCursorList,
    cursorList: cursorListResult.data?.blog?.cursor_list?.edges,
    cursorListLoading: cursorListResult.isFetching,
  };
}
